import {
  type ComponentType,
  type ReactNode,
  useCallback,
  useState,
} from "react";

type ModalBaseProps = {
  isOpen: boolean;
  onClose: (...args: unknown[]) => void;
};

export function useModal<P extends ModalBaseProps>(
  ModalComponent: ComponentType<P>,
  onCloseCallback: P["onClose"] | undefined = undefined
) {
  const [isOpen, setIsOpen] = useState(false);
  const [extraProps, setExtraProps] = useState<
    Omit<P, keyof ModalBaseProps> | undefined
  >(undefined);

  const openModal = useCallback((props?: Omit<P, keyof ModalBaseProps>) => {
    if (props) setExtraProps(props);
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(
    (...args: unknown[]) => {
      setIsOpen(false);
      onCloseCallback?.(...args);
    },
    [onCloseCallback]
  );

  const modal: ReactNode = (
    <ModalComponent
      {...(extraProps as P)}
      isOpen={isOpen}
      onClose={closeModal}
    />
  );

  return { isOpen, openModal, closeModal, modal };
}
